// Quản lý phiên đăng nhập Owner (token 24h) — NƠI DUY NHẤT đọc/ghi/xoá token.
// Mọi chỗ khác (baseQuery 401, PrivateRoute, timer hết hạn, nút Đăng xuất) đều
// đi qua đây để hành vi thống nhất: xoá đúng key phiên (KHÔNG đụng theme/ngôn ngữ)
// rồi đưa về màn login kèm thông báo phù hợp.

// Constants
import {
  AUTH_INFO,
  OWNER_TOKEN,
  OWNER_TOKEN_EXPIRES_AT,
} from '@app/constants/localStorage';
import { PAGE_URL } from '@app/constants/route';

// Cờ báo cho màn Login biết vì sao vừa bị đưa về đây (đặt ở sessionStorage nên
// sống sót qua full reload — khác location.state của react-router).
export const SESSION_NOTICE_KEY = 'ownerSessionNotice';

// Cờ "phiên NGHI hỏng": API owner thất bại ở tầng mạng nên KHÔNG đọc được status.
// Một 401 bị trình duyệt chặn vì thiếu header CORS trông giống hệt mất mạng ⇒ tuyệt đối
// KHÔNG tự đăng xuất theo cờ này (mất mạng thật thì đăng xuất là sai). Nó chỉ có một tác
// dụng: MỞ ĐƯỜNG cho người dùng vào lại màn login (xem useAuthRedirect) thay vì bị đá
// ngược về dashboard bởi cái token mà FE tưởng còn hạn.
export const AUTH_STUCK_KEY = 'ownerAuthStuck';
export const SESSION_NOTICE = {
  EXPIRED: 'expired', // hết hạn / 401 → hiện toast "phiên đã hết hạn"
  LOGGED_OUT: 'loggedOut', // người dùng chủ động đăng xuất → không cần toast lỗi
};

// Trừ hao lệch đồng hồ client/server: coi như hết hạn sớm 30s để tránh gọi API
// bằng token vừa chết (đằng nào cũng nhận 401).
const CLOCK_SKEW_MS = 30 * 1000;

// Chỉ xoá các key thuộc phiên đăng nhập.
const SESSION_KEYS = [OWNER_TOKEN, OWNER_TOKEN_EXPIRES_AT, AUTH_INFO];

export const getOwnerToken = () => localStorage.getItem(OWNER_TOKEN);

export const getDisplayName = () => localStorage.getItem(AUTH_INFO) || '';

// Đọc claim `exp` (giây epoch) của JWT. Token hỏng/không phải JWT → null.
const readJwtExpiry = (token) => {
  if (!token) {
    return null;
  }

  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    // base64url → base64 rồi decode (atob không hiểu '-' và '_').
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// Hạn token (ms epoch) — ƯU TIÊN claim `exp` của JWT, dự phòng mới tới `expiresAt`
// đã lưu lúc login. Không xác định được → null.
//
// Vì sao `exp` đứng trước (đổi thứ tự ngày 13/08/2026): `exp` là số giây epoch — tuyệt
// đối, không mang múi giờ, và CHÍNH NÓ là thứ backend đem ra so để trả 401. Còn
// `expiresAt` là chuỗi ngày-giờ: nếu server serialize thiếu offset/hậu tố Z thì
// `Date.parse` hiểu là giờ địa phương và lệch đúng bằng múi giờ của máy (đã từng lệch
// 7 giờ — xem DiOwnerJson.cs). Lấy `exp` trước ⇒ FE và backend luôn cùng một mốc, kể
// cả với token đã nằm sẵn trong localStorage từ trước khi backend được sửa.
export const getTokenExpiryTime = () => {
  const fromJwt = readJwtExpiry(getOwnerToken());
  if (fromJwt) {
    return fromJwt;
  }

  const saved = localStorage.getItem(OWNER_TOKEN_EXPIRES_AT);
  if (saved) {
    const parsed = Date.parse(saved);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
};

// Token đã hết hạn chưa? KHÔNG có token cũng coi là hết hạn.
// Có token nhưng không biết hạn (server không trả expiresAt, token không phải
// JWT) → coi là CÒN hạn, để 401 từ backend quyết định (tránh đá nhầm người dùng).
export const isSessionExpired = () => {
  if (!getOwnerToken()) {
    return true;
  }

  const expiry = getTokenExpiryTime();
  if (!expiry) {
    return false;
  }

  return Date.now() >= expiry - CLOCK_SKEW_MS;
};

// Số ms còn lại tới lúc hết hạn (đã trừ hao lệch giờ). null = không xác định.
export const getMsUntilExpiry = () => {
  const expiry = getTokenExpiryTime();
  if (!expiry) {
    return null;
  }

  return Math.max(0, expiry - CLOCK_SKEW_MS - Date.now());
};

// Lưu phiên sau khi POST /auth/owner-login thành công.
export const saveOwnerSession = ({ token, expiresAt, displayName }) => {
  localStorage.setItem(OWNER_TOKEN, token);

  // Hạn lưu kèm để tra nhanh; claim `exp` của JWT là nguồn CHÍNH (xem
  // getTokenExpiryTime), `expiresAt` chỉ dùng khi token không phải JWT.
  const expiry = readJwtExpiry(token) ?? (expiresAt ? Date.parse(expiresAt) : null);
  if (expiry && !Number.isNaN(expiry)) {
    localStorage.setItem(
      OWNER_TOKEN_EXPIRES_AT,
      new Date(expiry).toISOString()
    );
  } else {
    localStorage.removeItem(OWNER_TOKEN_EXPIRES_AT);
  }

  if (displayName) {
    localStorage.setItem(AUTH_INFO, displayName);
  }

  // Vào được rồi thì không còn thông báo phiên cũ nào để hiện nữa.
  clearSessionNotice();
  clearAuthStuck();
};

// Xoá phiên (giữ nguyên theme/ngôn ngữ và các cache nghiệp vụ khác).
export const clearOwnerSession = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const setSessionNotice = (notice) => {
  try {
    sessionStorage.setItem(SESSION_NOTICE_KEY, notice);
  } catch {
    // Safari private mode có thể chặn sessionStorage — mất toast, không mất logout.
  }
};

// Đọc & xoá cờ (one-shot) để toast không hiện lại khi vào màn login lần sau.
export const consumeSessionNotice = () => {
  try {
    const notice = sessionStorage.getItem(SESSION_NOTICE_KEY);
    sessionStorage.removeItem(SESSION_NOTICE_KEY);
    return notice;
  } catch {
    return null;
  }
};

export const clearSessionNotice = () => {
  try {
    sessionStorage.removeItem(SESSION_NOTICE_KEY);
  } catch {
    // bỏ qua
  }
};

export const setAuthStuck = () => {
  try {
    sessionStorage.setItem(AUTH_STUCK_KEY, '1');
  } catch {
    // bỏ qua
  }
};

export const isAuthStuck = () => {
  try {
    return sessionStorage.getItem(AUTH_STUCK_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearAuthStuck = () => {
  try {
    sessionStorage.removeItem(AUTH_STUCK_KEY);
  } catch {
    // bỏ qua
  }
};

// Kết thúc phiên và đưa về màn login.
// Dùng location.replace (KHÔNG phải navigate của router) là CỐ Ý: reload cả app
// nên mọi cache RTK Query / state Redux của chủ cũ bị xoá sạch, và `replace` khiến
// nút Back không quay lại được màn dữ liệu. Gọi được từ ngoài React (baseQuery).
export const forceLogout = (notice = SESSION_NOTICE.EXPIRED) => {
  clearOwnerSession();
  setSessionNotice(notice);
  // Đã dứt điểm bằng đăng xuất thật → không cần cờ "nghi hỏng" nữa.
  clearAuthStuck();

  if (typeof window === 'undefined') {
    return;
  }

  // Đang ở màn login rồi thì thôi, tránh reload vòng lặp.
  if (window.location.pathname === PAGE_URL.LOGIN) {
    return;
  }

  window.location.replace(PAGE_URL.LOGIN);
};
